import { useState } from "react";
import { Warning, FloppyDisk, HandArrowDown, Trash, Lock, ArrowsClockwise, MagnifyingGlass, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, registerError, qty } from "./registerApi";

// First week only: the stock already standing at the process. The figure is
// pre-filled from what SAP holds in the department's own godown (captured
// when the item was mapped) - Jun 2026 ask - and stays editable, because the
// drum at the line is the truth.
const OpeningBalanceCapture = ({ register, onSaved }) => {
  const seeded = Object.fromEntries(
    register.items.filter((i) => i.opening_seed !== null && i.opening_seed !== undefined)
      .map((i) => [i.product_id, String(i.opening_seed)]),
  );
  const [values, setValues] = useState(seeded);
  const [saving, setSaving] = useState(false);
  const prefilled = Object.keys(seeded).length;
  const asOf = register.opening_seed_as_of
    ? new Date(register.opening_seed_as_of).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;

  const save = async () => {
    setSaving(true);
    try {
      const balances = register.items.map((i) => ({
        product_id: i.product_id,
        quantity: Number(values[i.product_id] || 0),
      }));
      onSaved(await api.openingBalances(register.process.id, balances));
      toast.success("Opening balance saved", { description: "The week starts on your first consumption entry." });
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="opening-balance-capture">
      <div className="rounded-sm border border-[#FEDF89] bg-[#FFFAEB] p-3">
        <h2 className="font-heading text-base font-bold text-[#B54708]">Opening stock, week 1</h2>
        <p className="text-sm text-[#B54708]/90 mt-0.5">
          {prefilled > 0
            ? `Filled in from what SAP holds in ${register.department.logistics_area_id || "this godown"}${asOf ? `, as of ${asOf}` : ""}. Correct anything that does not match the drum at the line.`
            : "Enter what is physically standing at this process right now. Leave an item at zero if there is none."}
          {" "}From next week the counted figure carries forward on its own.
        </p>
      </div>
      {register.items.map((item) => (
        <div key={item.product_id} className="bg-white border border-[#D0D5DD] rounded-sm p-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-sm font-bold text-[#101828] truncate">{item.product_id}</div>
            <div className="text-xs text-[#667085] truncate">{item.description || "—"}</div>
            {item.opening_seed !== null && item.opening_seed !== undefined && (
              <div className="text-xs text-[#027A48] font-bold mt-0.5" data-testid={`opening-seed-${item.product_id}`}>
                SAP stock: {qty(item.opening_seed)} {item.unit_code}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Input
              type="number"
              step="any"
              inputMode="decimal"
              className="w-28 h-12 text-right text-xl font-bold tabular-nums"
              placeholder="0"
              value={values[item.product_id] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [item.product_id]: e.target.value }))}
              data-testid={`opening-input-${item.product_id}`}
            />
            <span className="text-xs font-bold text-[#475467] w-10">{item.unit_code}</span>
          </div>
        </div>
      ))}
      <Button className="w-full h-12 text-base" disabled={saving} onClick={save} data-testid="save-opening-balances">
        <FloppyDisk size={18} weight="bold" /> {saving ? "Saving..." : "Save opening stock"}
      </Button>
    </div>
  );
};

const ItemCard = ({ item, register, onSaved, onClaimHandover, locked }) => {
  const [value, setValue] = useState(item.entry_quantity ?? "");
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const dirty = String(value ?? "") !== String(item.entry_quantity ?? "");

  const save = async () => {
    setSaving(true);
    setBlocked(null);
    try {
      const data = await api.saveEntry({
        process_id: register.process.id,
        product_id: item.product_id,
        entry_date: register.date,
        quantity: Number(value || 0),
      });
      onSaved(data);
      toast.success(`${item.product_id} saved`, { description: `${qty(Number(value || 0))} ${item.unit_code} used` });
    } catch (err) {
      const e = registerError(err);
      setBlocked(e);
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      onSaved(await api.clearEntry({
        process_id: register.process.id,
        product_id: item.product_id,
        entry_date: register.date,
      }));
      setValue("");
      toast.success(`${item.product_id} cleared`);
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`bg-white border rounded-sm p-3 space-y-2 ${blocked ? "border-[#FDA29B]" : "border-[#D0D5DD]"}`}
      data-testid={`register-item-${item.product_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-sm font-bold text-[#101828] truncate">{item.product_id}</div>
          <div className="text-xs text-[#667085] truncate">{item.description || "—"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-bold tabular-nums text-[#1D2939] leading-none" data-testid={`balance-${item.product_id}`}>
            {qty(item.in_hand ?? item.balance)}
          </div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#667085]">
            {item.unit_code} {item.from_godown ? "in godown" : "in hand"}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Input
          type="number"
          step="any"
          inputMode="decimal"
          className="h-14 text-right text-2xl font-bold tabular-nums"
          placeholder={locked ? "Locked" : "Used today"}
          value={value}
          disabled={locked}
          onChange={(e) => setValue(e.target.value)}
          data-testid={`entry-input-${item.product_id}`}
        />
        <Button
          className="h-14 px-5 text-base shrink-0"
          disabled={saving || !dirty || locked}
          onClick={save}
          data-testid={`save-entry-${item.product_id}`}
        >
          <FloppyDisk size={18} weight="bold" /> Save
        </Button>
        {item.entry_quantity != null && !locked && (
          <Button variant="outline" className="h-14 px-3 shrink-0" disabled={saving} onClick={clear} data-testid={`clear-entry-${item.product_id}`}>
            <Trash size={16} />
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#667085]">
        <span>opening {qty(item.opening)}</span>
        {item.claimed > 0 && <span>taken to the line {qty(item.claimed)}</span>}
        {item.handovers > 0 && <span className="text-[#027A48]">brought in {qty(item.handovers)}</span>}
        <span>declared so far {qty(item.declared)}</span>
        {item.entry_quantity != null && (
          <span className="text-[#027A48] font-bold">saved {qty(item.entry_quantity)} {item.unit_code}</span>
        )}
      </div>

      {blocked && (
        <div className="rounded-sm border border-[#FDA29B] bg-[#FEF3F2] p-2 space-y-2" data-testid={`blocked-${item.product_id}`}>
          <p className="text-xs text-[#B42318] flex items-start gap-1.5">
            <Warning size={14} weight="bold" className="shrink-0 mt-0.5" /> {blocked.message}
          </p>
          {blocked.action === "claim_handover" && (
            <Button size="sm" variant="outline" className="w-full" onClick={() => onClaimHandover(item.product_id)} data-testid={`claim-from-block-${item.product_id}`}>
              <HandArrowDown size={14} weight="bold" /> Claim a handover
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Oct 26 2026, user: "match opening stock & Current stock based on Admin
// Physical Inventory so that week report show accurate". The head or
// supervisor pulls the week's opening back onto what SAP holds in the
// department's godown - the figure the week report reconciles against.
const MatchOpening = ({ register, onSaved }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const matchedAt = register.week.opening_matched_at
    ? new Date(register.week.opening_matched_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;

  const run = async () => {
    setBusy(true);
    try {
      const result = await api.matchOpening(register.process.id);
      onSaved(result.register);
      setConfirming(false);
      toast.success(
        result.changes.length
          ? `Opening matched - ${result.changes.length} item(s) changed`
          : "Opening already matches the godown",
        { description: result.changes.slice(0, 3).map((c) => `${c.product_id} ${qty(c.was)} → ${qty(c.now)}`).join(", ") || undefined },
      );
    } catch (e) {
      toast.error(registerError(e));
    } finally {
      setBusy(false);
    }
  };

  if (confirming) {
    return (
      <div className="space-y-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-2" data-testid="match-opening-confirm">
        <p className="text-xs text-[#B54708]">
          This week&apos;s opening becomes what SAP holds in {register.department.logistics_area_id || "the department godown"} right
          now, plus what this week already declared and less what was claimed in. Figures already saved for a day are untouched.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Button className="h-11" disabled={busy} onClick={run} data-testid="confirm-match-opening">
            {busy ? "Matching..." : "Yes, match it"}
          </Button>
          <Button variant="outline" className="h-11" onClick={() => setConfirming(false)} data-testid="cancel-match-opening">
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  // Oct 26 2026: "opening 0" against a godown that is visibly holding 2,040
  // KGM is the whole complaint, so the page says so in plain words instead of
  // a quiet hint nobody reads.
  const outOfStep = register.items.filter((i) => (i.in_hand ?? 0) > 0 && !((i.opening ?? 0) > 0));
  if (outOfStep.length) {
    return (
      <div className="space-y-2 bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-2.5" data-testid="opening-out-of-step">
        <p className="text-xs text-[#B42318]">
          <span className="font-bold">
            Opening reads 0 for {outOfStep.length} material{outOfStep.length > 1 ? "s" : ""} the godown is holding
          </span>{" "}
          ({outOfStep.slice(0, 3).map((i) => i.product_id).join(", ")}
          {outOfStep.length > 3 ? ` +${outOfStep.length - 3} more` : ""}), so the week report cannot add up.
          {register.can_match_opening
            ? " Match the opening to the godown to put it right."
            : " An administrator has to match it to the godown - ask IT to run it for this week."}
        </p>
        {register.can_match_opening && (
          <Button className="w-full h-11 bg-[#B42318] hover:bg-[#912018]" onClick={() => setConfirming(true)} data-testid="match-opening-btn">
            <ArrowsClockwise size={16} weight="bold" /> Match opening to godown
          </Button>
        )}
      </div>
    );
  }
  if (!register.can_match_opening) {
    return matchedAt ? (
      <span className="text-[11px] text-[#667085]" data-testid="opening-matched-at">Opening matched to the godown on {matchedAt}</span>
    ) : null;
  }
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      {matchedAt
        ? <span className="text-[11px] text-[#667085]" data-testid="opening-matched-at">Opening matched to the godown on {matchedAt}</span>
        : <span className="text-[11px] text-[#667085]">Opening looks wrong against the godown?</span>}
      <Button variant="outline" size="sm" onClick={() => setConfirming(true)} data-testid="match-opening-btn">
        <ArrowsClockwise size={14} weight="bold" /> Match opening to godown
      </Button>
    </div>
  );
};

export const DailyRegister = ({ register, onSaved, onClaimHandover }) => {
  // Oct 27 2026, user: "Add a search box for find godown item in this page" -
  // a department carries dozens of chemicals and the man at the line knows the
  // code or the name, not the position in the list.
  const [query, setQuery] = useState("");
  if (!register) return null;
  if (register.items.length === 0) {
    return (
      <div className="rounded-sm border border-[#D0D5DD] bg-white p-6 text-center text-sm text-[#667085]" data-testid="register-no-items">
        No materials are mapped to this process yet. An administrator maps them under Setup.
      </div>
    );
  }
  if (register.needs_opening_balance) {
    return <OpeningBalanceCapture register={register} onSaved={onSaved} />;
  }
  // Oct 26 2026: a past day closes at 10:00 AM the next morning, for
  // everybody - the figures stay readable, the inputs do not.
  const windowClosed = register.entry_window ? !register.entry_window.date_open : false;
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? register.items.filter((i) => `${i.product_id} ${i.description || ""}`.toLowerCase().includes(needle))
    : register.items;
  return (
    <div className="space-y-3" data-testid="daily-register">
      {windowClosed ? (
        <p className="text-xs font-bold text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-2 py-1.5 flex items-start gap-1.5" data-testid="entry-window-closed-notice">
          <Lock size={13} weight="bold" className="shrink-0 mt-0.5" />
          This day closed at {register.entry_window.cutoff_label} the next morning, so its figures are fixed. Record today&apos;s
          consumption instead.
        </p>
      ) : register.day_locked ? (
        <p className="text-xs font-bold text-[#027A48] bg-[#ECFDF3] border border-[#A6F4C5] rounded-sm px-2 py-1.5 flex items-start gap-1.5" data-testid="day-locked-notice">
          <Lock size={13} weight="bold" className="shrink-0 mt-0.5" />
          {register.day_posting?.status === "pending"
            ? "This day is being posted to SAP right now, so its figures are held until it finishes."
            : register.day_posting?.status === "unknown"
              ? "SAP never confirmed this day's posting, so the figures are frozen until a supervisor settles it against SAP."
              : "This day has gone to SAP, so its figures are fixed. The head or supervisor can add more consumption to it once, below."}
        </p>
      ) : (
        <p className="text-xs text-[#667085]">
          One figure per item per day, in the material's SAP unit. A day with nothing used needs no entry.
        </p>
      )}
      {register.week.status !== "closed" && (
        <MatchOpening register={register} onSaved={onSaved} />
      )}
      <div className="relative">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3] pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${register.items.length} godown items by code or name`}
          className="h-11 pl-9 pr-9"
          data-testid="item-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#667085] hover:text-[#101828]"
            aria-label="Clear search"
            data-testid="item-search-clear"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>
      {needle && (
        <p className="text-[11px] text-[#667085]" data-testid="item-search-count">
          {shown.length} of {register.items.length} items
        </p>
      )}
      {shown.length === 0 ? (
        <div className="rounded-sm border border-[#D0D5DD] bg-white p-6 text-center text-sm text-[#667085]" data-testid="item-search-empty">
          No mapped material matches &quot;{query.trim()}&quot;. Only materials an administrator mapped to this process appear here.
        </div>
      ) : shown.map((item) => (
        <ItemCard
          key={item.product_id}
          item={item}
          register={register}
          onSaved={onSaved}
          onClaimHandover={onClaimHandover}
          locked={register.day_locked || windowClosed}
        />
      ))}
    </div>
  );
};
